/**
 * ErrorLogger Service
 * 
 * Servicio centralizado para capturar, formatear y almacenar errores en localStorage.
 * Mantiene un registro de los últimos 100 errores con contexto completo.
 */

class ErrorLogger {
    constructor() {
        this.storageKey = 'app_error_logs';
        this.maxLogs = 100;
    }

    /**
     * Registra un error con contexto adicional
     * @param {Error} error - El error a registrar
     * @param {Object} context - Contexto adicional (operación, tipo, etc.)
     */
    log(error, context = {}) {
        const errorLog = {
            id: Date.now() + Math.random(), // ID único
            timestamp: new Date().toISOString(),
            message: error?.message || String(error) || 'Unknown error',
            stack: error?.stack || '',
            type: error?.name || 'Error',
            context: {
                url: window.location.href,
                pathname: window.location.pathname,
                userAgent: navigator.userAgent,
                timestamp: new Date().toLocaleString('es-ES'),
                ...context
            }
        };

        this.saveToStorage(errorLog);

        // En desarrollo, también mostrar en consola
        if (process.env.NODE_ENV === 'development') {
            console.error('🔴 Error logged:', errorLog);
        }

        return errorLog;
    }

    /**
     * Guarda el error en localStorage
     * @param {Object} errorLog - El log de error a guardar
     */
    saveToStorage(errorLog) {
        try {
            const logs = this.getLogs();
            logs.unshift(errorLog);

            // Mantener solo los últimos 100 errores
            const trimmedLogs = logs.slice(0, this.maxLogs);

            localStorage.setItem(this.storageKey, JSON.stringify(trimmedLogs));
        } catch (e) {
            console.error('Failed to save error log to localStorage:', e);
        }
    }

    /**
     * Obtiene todos los logs de errores
     * @returns {Array} Array de logs de errores
     */
    getLogs() {
        try {
            const logs = localStorage.getItem(this.storageKey);
            return logs ? JSON.parse(logs) : [];
        } catch (e) {
            console.error('Failed to retrieve error logs:', e);
            return [];
        }
    }

    /**
     * Limpia todos los logs de errores
     */
    clearLogs() {
        try {
            localStorage.removeItem(this.storageKey);
            if (process.env.NODE_ENV === 'development') {
                console.log('✅ Error logs cleared');
            }
        } catch (e) {
            console.error('Failed to clear error logs:', e);
        }
    }

    /**
     * Exporta los logs como archivo JSON
     */
    exportLogs() {
        try {
            const logs = this.getLogs();
            const blob = new Blob([JSON.stringify(logs, null, 2)],
                { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `error-logs-${Date.now()}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Failed to export error logs:', e);
        }
    }

    /**
     * Obtiene estadísticas de los errores
     * @returns {Object} Estadísticas de errores
     */
    getStats() {
        const logs = this.getLogs();
        const stats = {
            total: logs.length,
            byType: {},
            byOperation: {},
            last24Hours: 0
        };

        const oneDayAgo = Date.now() - (24 * 60 * 60 * 1000);

        logs.forEach(log => {
            // Por tipo
            stats.byType[log.type] = (stats.byType[log.type] || 0) + 1;

            // Por operación
            const operation = log.context?.operation || 'unknown';
            stats.byOperation[operation] = (stats.byOperation[operation] || 0) + 1;

            // Últimas 24 horas
            if (new Date(log.timestamp).getTime() > oneDayAgo) {
                stats.last24Hours++;
            }
        });

        return stats;
    }
}

// Exportar instancia singleton
export default new ErrorLogger();
